import {useEffect, useRef, useState} from 'react'
import {Badge, Button, Col, Form, Modal, Row, Spinner} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'
import {prependBackendUrl} from '../../../gql/client'
import {
  CREATE_ACA_VERIFICATION,
  UPDATE_ACA_VERIFICATION,
  IResCreateACAVerification,
  IResUpdateACAVerification,
} from '../../../gql/mutations/acaVerificationsMutations'
import {ACAVerificationRecord} from '../../../gql/queries/acaVerificationsQuery'
import {GET_ONE_POLICY_ATTACHMENTS_API} from '../../../gql/queries/policiesApiQueries'
import {EdgeMemberPolicy, IEdgesAttachments} from '../../../types/policiesApi'
import {ModalAddDocsPolicy} from './documents/ModalAddDocsPolicy'

type ResultColor = 'green' | 'yellow' | 'red' | 'blue'

export interface IModalACAVerificationProps {
  isOpen: boolean
  onClose: () => void
  policyId: string
  documentsDueDate?: string | null
  hasOpenTickets: boolean
  /** Pass an existing record to open in edit mode */
  verificationToEdit?: ACAVerificationRecord
  /** Called after a successful save so the tab can refresh */
  onSaved?: () => void
  canChange?: boolean
  members?: EdgeMemberPolicy[]
  /** Documents filtered by the verification's date — shown in edit/view mode */
  verificationDocs?: IEdgesAttachments[]
}

interface ACAForm {
  paymentStatus: 'up_to_date' | 'pending' | 'zero_payment' | ''
  paymentMethod: 'updated_in_system' | 'no_changes' | ''
  billingActionTaken: string
  pendingDocuments: 'yes' | 'no' | 'na' | ''
  docsActionTaken: string
  incomeModification: 'done' | 'no_changes_needed' | ''
  contactInfo: 'verified' | 'updated' | ''
  openTicketsDetail: string
  additionalNotes: string
}

const INIT_FORM: ACAForm = {
  paymentStatus: '',
  paymentMethod: '',
  billingActionTaken: '',
  pendingDocuments: '',
  docsActionTaken: '',
  incomeModification: '',
  contactInfo: '',
  openTicketsDetail: '',
  additionalNotes: '',
}

/**
 * Auto-compute the incentive segment based on billing + document answers.
 *
 * Green  – Payment up to date + no pending documents
 * Yellow – Payment up to date + pending documents
 * Red    – Payment pending/not confirmed  OR  $0 payment + pending docs (does not qualify)
 * Blue   – $0 premium + no/NA pending documents (doc-only follow-up segment)
 */
const computeResult = (paymentStatus: string, pendingDocuments: string): ResultColor | '' => {
  if (!paymentStatus || !pendingDocuments) return ''
  if (paymentStatus === 'zero_payment' && pendingDocuments === 'yes') return 'red'
  if (paymentStatus === 'zero_payment') return 'blue'
  if (paymentStatus === 'up_to_date' && (pendingDocuments === 'no' || pendingDocuments === 'na'))
    return 'green'
  if (paymentStatus === 'up_to_date' && pendingDocuments === 'yes') return 'yellow'
  if (paymentStatus === 'pending') return 'red'
  return ''
}

const RESULT_CONFIG: Record<ResultColor, {label: string; bg: string; textClass: string}> = {
  green:  {label: 'Paid & Completed',      bg: '#198754', textClass: 'text-white'},
  yellow: {label: 'Paid / Pending Docs',   bg: '#ffc107', textClass: 'text-dark'},
  red:    {label: 'Unpaid / Pending',      bg: '#dc3545', textClass: 'text-white'},
  blue:   {label: '$0 Premium / Completed',bg: '#0d6efd', textClass: 'text-white'},
}

const formatDeadline = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '—'
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

const recordToForm = (v: ACAVerificationRecord): ACAForm => ({
  paymentStatus: (v.paymentStatus as ACAForm['paymentStatus']) || '',
  paymentMethod: (v.paymentMethod as ACAForm['paymentMethod']) || '',
  billingActionTaken: v.billingActionTaken || '',
  pendingDocuments: (v.pendingDocuments as ACAForm['pendingDocuments']) || '',
  docsActionTaken: v.docsActionTaken || '',
  incomeModification: (v.incomeModification as ACAForm['incomeModification']) || '',
  contactInfo: (v.contactInfo as ACAForm['contactInfo']) || '',
  openTicketsDetail: v.openTicketsDetail || '',
  additionalNotes: v.additionalNotes || '',
})

export const ModalACAVerification: React.FC<IModalACAVerificationProps> = ({
  isOpen,
  onClose,
  policyId,
  documentsDueDate,
  hasOpenTickets,
  verificationToEdit,
  onSaved,
  canChange,
  members = [],
  verificationDocs,
}) => {
  const isEditMode = !!verificationToEdit
  const readOnly = isEditMode && !canChange

  const [isUploadDocsOpen, setIsUploadDocsOpen] = useState(false)
  const [sessionDocs, setSessionDocs] = useState<IEdgesAttachments[]>([])
  const knownDocIds = useRef<Set<string>>(new Set())

  const [fetchDocs] = useLazyQuery<{
    policiesById: {policyAttachmentsSet: {edges: IEdgesAttachments[]}}
  }>(GET_ONE_POLICY_ATTACHMENTS_API, {fetchPolicy: 'no-cache'})

  // Snapshot existing doc IDs when the modal opens so we can diff after upload
  useEffect(() => {
    if (!isOpen || !policyId) return
    setSessionDocs([])
    knownDocIds.current = new Set()
    fetchDocs({variables: {id: parseId(policyId)}}).then((res) => {
      const ids =
        res.data?.policiesById?.policyAttachmentsSet?.edges.map((e) => e.node.id) || []
      knownDocIds.current = new Set(ids)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, policyId])

  const handleAfterUpload = async () => {
    const res = await fetchDocs({variables: {id: parseId(policyId)}})
    const allEdges = res.data?.policiesById?.policyAttachmentsSet?.edges || []
    const newEdges = allEdges.filter((e) => !knownDocIds.current.has(e.node.id))
    newEdges.forEach((e) => knownDocIds.current.add(e.node.id))
    if (newEdges.length > 0) setSessionDocs((prev) => [...prev, ...newEdges])
  }

  const [form, setForm] = useState<ACAForm>(
    verificationToEdit ? recordToForm(verificationToEdit) : {...INIT_FORM}
  )

  // Re-populate form whenever the edit target changes (e.g., user opens a different row)
  useEffect(() => {
    setForm(verificationToEdit ? recordToForm(verificationToEdit) : {...INIT_FORM})
  }, [verificationToEdit])

  const set = (field: keyof ACAForm, value: string) => {
    if (readOnly) return
    setForm((prev) => ({...prev, [field]: value}))
  }

  const [gqlCreate, {loading: loadingCreate}] =
    useMutation<IResCreateACAVerification>(CREATE_ACA_VERIFICATION)
  const [gqlUpdate, {loading: loadingUpdate}] =
    useMutation<IResUpdateACAVerification>(UPDATE_ACA_VERIFICATION)

  const loading = loadingCreate || loadingUpdate

  const result = computeResult(form.paymentStatus, form.pendingDocuments)
  const resultConfig = result ? RESULT_CONFIG[result] : null

  const buildInput = () => ({
    policyId: Number(parseId(policyId)),
    paymentStatus: form.paymentStatus,
    paymentMethod: form.paymentMethod,
    billingActionTaken: form.billingActionTaken,
    pendingDocuments: form.pendingDocuments,
    docsActionTaken: form.docsActionTaken,
    incomeModification: form.incomeModification,
    contactInfo: form.contactInfo,
    result: result || '',
    openTicketsDetail: form.openTicketsDetail,
    additionalNotes: form.additionalNotes,
  })

  const handleSave = async () => {
    if (readOnly) return

    if (isEditMode && verificationToEdit) {
      const {data} = await gqlUpdate({
        variables: {id: Number(parseId(verificationToEdit.id)), data: buildInput()},
      })
      if (data?.updatePolicyAcaVerification?.success) {
        toast.success('ACA Verification updated successfully')
        onSaved?.()
        handleClose()
      } else {
        const msg = data?.updatePolicyAcaVerification?.errors?.[0]?.message || 'Update failed'
        toast.error(msg)
      }
    } else {
      const {data} = await gqlCreate({variables: {data: buildInput()}})
      if (data?.createPolicyAcaVerification?.success) {
        toast.success('ACA Verification saved successfully')
        onSaved?.()
        handleClose()
      } else {
        const msg = data?.createPolicyAcaVerification?.errors?.[0]?.message || 'Save failed'
        toast.error(msg)
      }
    }
  }

  const handleClose = () => {
    setForm({...INIT_FORM})
    onClose()
  }

  return (
    <Modal show={isOpen} onHide={handleClose} size='lg' scrollable>
      <Modal.Header closeButton className='bg-light'>
        <Modal.Title>
          <i className='bi bi-clipboard2-check me-2 text-primary' />
          {isEditMode ? 'Edit ACA Verification' : 'ACA Verification Checklist'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* ── 1. BILLING STATUS ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            1. Billing Status
          </h6>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>Payment Status</Form.Label>
            <div className='d-flex flex-wrap gap-3'>
              {(
                [
                  {value: 'up_to_date', label: 'Up to date'},
                  {value: 'pending', label: 'Pending'},
                  {value: 'zero_payment', label: '$0 Payment'},
                ] as const
              ).map(({value, label}) => (
                <Form.Check
                  key={value}
                  type='radio'
                  id={`paymentStatus_${value}`}
                  name='paymentStatus'
                  label={label}
                  disabled={readOnly}
                  checked={form.paymentStatus === value}
                  onChange={() => set('paymentStatus', value)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>Payment Method</Form.Label>
            <div className='d-flex flex-wrap gap-3'>
              {(
                [
                  {value: 'updated_in_system', label: 'Updated in System'},
                  {value: 'no_changes', label: 'No changes'},
                ] as const
              ).map(({value, label}) => (
                <Form.Check
                  key={value}
                  type='radio'
                  id={`paymentMethod_${value}`}
                  name='paymentMethod'
                  label={label}
                  disabled={readOnly}
                  checked={form.paymentMethod === value}
                  onChange={() => set('paymentMethod', value)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>Action Taken</Form.Label>
            <Form.Control
              type='text'
              placeholder='e.g., New card charged / Payment due date reminder sent'
              disabled={readOnly}
              value={form.billingActionTaken}
              onChange={(e) => set('billingActionTaken', e.target.value)}
            />
          </Form.Group>
        </section>

        {/* ── 2. DOCUMENT MANAGEMENT ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            2. Document Management (Marketplace)
          </h6>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>Pending Documents</Form.Label>
            <div className='d-flex flex-wrap gap-3'>
              {(
                [
                  {value: 'yes', label: 'Yes'},
                  {value: 'no', label: 'No'},
                  {value: 'na', label: 'N/A'},
                ] as const
              ).map(({value, label}) => (
                <Form.Check
                  key={value}
                  type='radio'
                  id={`pendingDocuments_${value}`}
                  name='pendingDocuments'
                  label={label}
                  disabled={readOnly}
                  checked={form.pendingDocuments === value}
                  onChange={() => set('pendingDocuments', value)}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className='mb-3'>
            <Form.Label className='fw-semibold mb-1'>Action Taken</Form.Label>
            <Form.Control
              type='text'
              placeholder='e.g., I-94 photo uploaded / Income letter requested'
              disabled={readOnly}
              value={form.docsActionTaken}
              onChange={(e) => set('docsActionTaken', e.target.value)}
            />
          </Form.Group>

          <div className='mb-0'>
            <Form.Label className='fw-semibold mb-1'>Marketplace Deadline</Form.Label>
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-calendar-event text-muted' />
              <span className={`fw-medium ${!documentsDueDate ? 'text-muted fst-italic' : ''}`}>
                {formatDeadline(documentsDueDate)}
              </span>
              {documentsDueDate && (
                <Badge bg='secondary' className='ms-1'>
                  from policy
                </Badge>
              )}
            </div>
          </div>
        </section>

        {/* ── 3. PROFILE UPDATE ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            3. Profile Update
          </h6>

          <Row>
            <Col sm={6}>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-semibold mb-1'>Income Modification</Form.Label>
                <div className='d-flex flex-wrap gap-3'>
                  {(
                    [
                      {value: 'done', label: 'Done'},
                      {value: 'no_changes_needed', label: 'No changes needed'},
                    ] as const
                  ).map(({value, label}) => (
                    <Form.Check
                      key={value}
                      type='radio'
                      id={`incomeModification_${value}`}
                      name='incomeModification'
                      label={label}
                      disabled={readOnly}
                      checked={form.incomeModification === value}
                      onChange={() => set('incomeModification', value)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
            <Col sm={6}>
              <Form.Group className='mb-3'>
                <Form.Label className='fw-semibold mb-1'>Contact Information</Form.Label>
                <div className='d-flex flex-wrap gap-3'>
                  {(
                    [
                      {value: 'verified', label: 'Verified'},
                      {value: 'updated', label: 'Updated'},
                    ] as const
                  ).map(({value, label}) => (
                    <Form.Check
                      key={value}
                      type='radio'
                      id={`contactInfo_${value}`}
                      name='contactInfo'
                      label={label}
                      disabled={readOnly}
                      checked={form.contactInfo === value}
                      onChange={() => set('contactInfo', value)}
                    />
                  ))}
                </div>
              </Form.Group>
            </Col>
          </Row>
        </section>

        {/* ── 4. FINAL MANAGEMENT STATUS ── */}
        <section className='mb-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            4. Final Management Status
          </h6>

          <Row>
            <Col sm={6} className='mb-3'>
              <Form.Label className='fw-semibold mb-2'>Result</Form.Label>
              {resultConfig ? (
                <div
                  className={`rounded px-3 py-2 d-inline-flex align-items-center fw-semibold ${resultConfig.textClass}`}
                  style={{backgroundColor: resultConfig.bg}}
                >
                  {resultConfig.label}
                </div>
              ) : (
                <p className='text-muted fst-italic small mb-0'>
                  Auto-calculated once Payment Status and Pending Documents are selected.
                </p>
              )}
            </Col>

            <Col sm={6} className='mb-3'>
              <Form.Label className='fw-semibold mb-2'>Open Tickets</Form.Label>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <Badge bg={hasOpenTickets ? 'danger' : 'success'} className='px-2 py-1'>
                  {hasOpenTickets ? 'Yes' : 'No'}
                </Badge>
                <span className='text-muted small'>
                  {hasOpenTickets
                    ? 'This policy has active tickets'
                    : 'No open tickets on this policy'}
                </span>
              </div>
              {hasOpenTickets && (
                <Form.Control
                  type='text'
                  placeholder='Describe the reason for open ticket(s)...'
                  disabled={readOnly}
                  value={form.openTicketsDetail}
                  onChange={(e) => set('openTicketsDetail', e.target.value)}
                />
              )}
            </Col>
          </Row>
        </section>

        {/* ── ADDITIONAL NOTES ── */}
        <section className='mb-2'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            Additional Notes
          </h6>
          <Form.Control
            as='textarea'
            rows={3}
            placeholder='Write any relevant details from the conversation with the client...'
            disabled={readOnly}
            value={form.additionalNotes}
            onChange={(e) => set('additionalNotes', e.target.value)}
          />
        </section>

        {/* ── SUPPORTING DOCUMENTS ── */}
        <section className='mb-2 mt-4'>
          <h6 className='fw-bold text-uppercase text-primary border-bottom pb-1 mb-3'>
            Supporting Documents
          </h6>

          {isEditMode ? (
            /* View mode: show documents uploaded on the verification's date */
            <>
              {verificationDocs && verificationDocs.length > 0 ? (
                <div className='d-flex flex-column gap-2'>
                  {verificationDocs.map(({node}) => (
                    <div
                      key={node.id}
                      className='d-flex align-items-center gap-2 border rounded px-3 py-2'
                    >
                      <i className='bi bi-file-earmark text-muted fs-5' />
                      <div className='flex-grow-1 overflow-hidden'>
                        <div className='fw-medium text-truncate'>{node.name}</div>
                        {node.memberPolicy && (
                          <div className='text-muted small'>
                            {node.memberPolicy.firstName} {node.memberPolicy.lastName}
                          </div>
                        )}
                      </div>
                      {node.attachments && (
                        <a
                          href={prependBackendUrl(node.attachments)}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-sm btn-outline-secondary'
                        >
                          <i className='bi bi-download' />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className='text-muted small mb-0'>No documents found for this verification date.</p>
              )}
            </>
          ) : (
            /* Create mode: upload button + session docs */
            <>
              <Button
                variant='outline-primary'
                size='sm'
                className='mb-3'
                onClick={() => setIsUploadDocsOpen(true)}
              >
                <i className='bi bi-upload me-1' />
                Upload Files
              </Button>
              {sessionDocs.length > 0 && (
                <div className='d-flex flex-column gap-2'>
                  {sessionDocs.map(({node}) => (
                    <div
                      key={node.id}
                      className='d-flex align-items-center gap-2 border rounded px-3 py-2'
                    >
                      <i className='bi bi-file-earmark text-muted fs-5' />
                      <div className='flex-grow-1 overflow-hidden'>
                        <div className='fw-medium text-truncate'>{node.name}</div>
                        {node.memberPolicy && (
                          <div className='text-muted small'>
                            {node.memberPolicy.firstName} {node.memberPolicy.lastName}
                          </div>
                        )}
                      </div>
                      {node.attachments && (
                        <a
                          href={prependBackendUrl(node.attachments)}
                          target='_blank'
                          rel='noreferrer'
                          className='btn btn-sm btn-outline-secondary'
                        >
                          <i className='bi bi-download' />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {sessionDocs.length === 0 && (
                <p className='text-muted small mb-0'>No documents uploaded in this session.</p>
              )}
            </>
          )}
        </section>
      </Modal.Body>

      <Modal.Footer>
        {resultConfig && (
          <span
            className={`badge fs-6 px-3 py-2 me-auto ${resultConfig.textClass}`}
            style={{backgroundColor: resultConfig.bg}}
          >
            {resultConfig.label}
          </span>
        )}
        <Button variant='secondary' onClick={handleClose}>
          Close
        </Button>
        {!readOnly && (
          <Button variant='primary' onClick={handleSave} disabled={loading}>
            {loading && <Spinner animation='border' size='sm' className='me-2' />}
            <i className='bi bi-clipboard2-check me-1' />
            {isEditMode ? 'Update Verification' : 'Save Verification'}
          </Button>
        )}
      </Modal.Footer>

      {isUploadDocsOpen && (
        <ModalAddDocsPolicy
          id={policyId}
          isOpen={isUploadDocsOpen}
          onClose={() => setIsUploadDocsOpen(false)}
          refetch={handleAfterUpload}
          members={members}
        />
      )}
    </Modal>
  )
}
